import {RadioMetadata} from '@theme/flopradio-timing';

export class RadioController {
  constructor(root, labels = {}, audio = root.querySelector('[data-role="audio"]')) {
    this.audio=audio;this.labels=labels;
    this.attach(root);
    this.life=new AbortController();this.wanted=false;this.attempts=0;this.generation=0;this.lastProgress=Date.now();
    const on=(target,event,fn)=>target?.addEventListener(event,fn,{signal:this.life.signal});
    on(this.audio,'timeupdate',()=>{this.recordProgress();this.metadata?.playbackTick();});
    on(this.audio,'playing',()=>{if(!this.wanted)return;this.connecting=false;this.recordProgress();this.clearTimers();this.watchdog=setTimeout(()=>this.checkProgress(),5000);this.show('playing');this.mediaState('playing');});
    on(this.audio,'waiting',()=>this.buffering());on(this.audio,'stalled',()=>this.buffering());
    on(this.audio,'error',()=>{this.retry();});on(this.audio,'ended',()=>{this.retry();});
    on(this.audio,'pause',()=>{if(this.wanted&&!this.connecting&&!this.retryTimer)this.retry();});
    on(window,'offline',()=>{if(this.wanted){this.clearTimers();this.show('offline');}});
    on(window,'online',()=>{if(this.wanted)void this.connect(true);});
    on(document,'visibilitychange',()=>{if(!document.hidden&&this.wanted&&Date.now()-this.lastProgress>20000)void this.connect(true);});
    this.show('ready');this.updateUptime();this.uptime=setInterval(()=>this.updateUptime(),1000);
    if(root.querySelector('[data-radio-now]'))this.metadata=new RadioMetadata(this);
  }
  attach(root){
    this.controls?.abort();this.controls=new AbortController();this.root=root;
    this.playButton=root.querySelector('[data-role="play"]');this.pauseButton=root.querySelector('[data-role="pause"]');
    this.volume=root.querySelector('[data-role="volume"]');this.status=root.querySelector('[data-role="status"]');
    this.buffer=root.querySelector('[data-role="buffer"]');this.timer=root.querySelector('[data-role="timer"]');
    const on=(target,event,fn)=>target?.addEventListener(event,fn,{signal:this.controls.signal});
    on(this.playButton,'click',()=>void this.play());on(this.pauseButton,'click',()=>this.pause());
    on(this.volume,'input',()=>{this.audio.volume=Number(this.volume.value);});
    if(this.volume)this.volume.value=String(this.audio.volume);
    if(this.state)this.show(this.state);
    this.metadata?.attach(root);
  }
  show(state){
    this.state=state;
    this.root.dataset.playback=state;
    if(this.status)this.status.textContent=this.labels[state]||state;
    this.buffer?.classList.toggle('is-active',['connecting','buffering','retrying'].includes(state));
    this.playButton?.setAttribute('aria-pressed',String(state==='playing'));
    if(typeof CustomEvent!=='undefined')document.dispatchEvent(new CustomEvent('flopradio:state',{detail:{controller:this,state}}));
  }
  clearTimers(){clearTimeout(this.retryTimer);clearTimeout(this.watchdog);this.retryTimer=null;this.watchdog=null;}
  async play(){
    if(this.wanted&&(this.connecting||!this.audio.paused&&!this.audio.error))return;
    this.wanted=true;this.attempts=0;await this.connect(true);
  }
  async connect(reload=false){
    if(!this.wanted||this.destroyed)return;
    if(navigator.onLine===false){this.show('offline');return;}
    this.clearTimers();this.connecting=true;const generation=++this.generation;this.show('connecting');
    if(!this.audio.getAttribute('src')||reload){
      const url=new URL(this.root.dataset.streamUrl);
      url.searchParams.set('_flop_live',`${Date.now()}-${generation}`);
      this.audio.src=url.href;this.audio.load();
    }
    this.lastProgress=Date.now();this.lastPosition=this.audio.currentTime;this.healthySince=Date.now();this.watchdog=setTimeout(()=>this.checkProgress(),5000);
    try{await this.audio.play();if(generation!==this.generation||!this.wanted)return;this.setupMedia();}
    catch(error){if(generation!==this.generation||!this.wanted)return;this.connecting=false;
      if(error.name==='NotAllowedError'){this.wanted=false;this.clearTimers();this.show('tap_play');}
      else this.retry();
    }
  }
  buffering(){
    if(!this.wanted||this.audio.readyState>=3&&!this.audio.paused)return;
    this.show('buffering');if(!this.watchdog)this.watchdog=setTimeout(()=>this.checkProgress(),20000);
  }
  checkProgress(){
    this.watchdog=null;if(!this.wanted)return;
    this.recordProgress();
    if(Date.now()-this.lastProgress<20000){
      if(Date.now()-this.healthySince>=30000)this.attempts=0;
      this.watchdog=setTimeout(()=>this.checkProgress(),5000);return;
    }
    this.retry();
  }
  recordProgress(){
    const position=this.audio.currentTime;
    if(Number.isFinite(position)&&position!==this.lastPosition){this.lastProgress=Date.now();this.lastPosition=position;}
  }
  retry(){
    if(!this.wanted||this.retryTimer||this.destroyed)return;
    clearTimeout(this.watchdog);this.watchdog=null;this.connecting=false;
    if(navigator.onLine===false){this.show('offline');return;}
    if(this.attempts>=6){this.wanted=false;this.show('failed');return;}
    const delay=Math.min(30000,2000*2**this.attempts++);this.show('retrying');
    this.retryTimer=setTimeout(()=>{this.retryTimer=null;if(this.wanted)void this.connect(true);},delay);
  }
  pause(){this.wanted=false;this.connecting=false;this.generation++;this.clearTimers();this.audio.pause();this.audio.removeAttribute('src');this.audio.load();this.show('paused');this.mediaState('paused');}
  mediaState(state){try{if(navigator.mediaSession)navigator.mediaSession.playbackState=state;}catch{/* Unsupported media controls must not interrupt audio. */}}
  setupMedia(){
    if(!navigator.mediaSession)return;
    try{
      const image=this.root.querySelector('[data-role="thumbnail"]');
      const station=this.root.dataset.artist||'FlopRadio®',track=this.track;
      const artist=[track?.original_artist,track?.remixer&&track.remixer!==track.original_artist?(this.labels.remix_by||'Remix by')+' '+track.remixer:null].filter(Boolean).join(' · ')||station;
      const album=[station,track?.showTitle||(track?.presenter?(this.labels.with_presenter||'With')+' '+track.presenter:null),track?.version].filter(Boolean).join(' · ');
      const artwork=this.root.dataset.mediaArtwork?[{src:this.root.dataset.mediaArtwork,sizes:'1000x1000',type:'image/png'}]:image?.src?[{src:image.src,...(image.naturalWidth&&image.naturalHeight?{sizes:image.naturalWidth+'x'+image.naturalHeight}:{})}]:[];
      if(typeof MediaMetadata!=='undefined')navigator.mediaSession.metadata=new MediaMetadata({title:track?.title||this.root.dataset.title,artist,album,artwork});
    }catch{/* Metadata support must not interrupt audio or transport controls. */}
    for(const [action,handler] of [['play',()=>void this.play()],['pause',()=>this.pause()],['stop',()=>this.pause()],['seekbackward',null],['seekforward',null],['seekto',null]]){
      try{navigator.mediaSession.setActionHandler(action,handler);}catch{/* Unsupported actions differ across Safari and other browsers. */}
    }
  }
  updateUptime(){
    if(!this.timer)return;const launch=Date.parse(this.root.dataset.launchTime);if(!Number.isFinite(launch))return;
    const seconds=Math.max(0,Math.floor((Date.now()-launch)/1000));
    this.timer.textContent=`${this.labels.live_for||''} ${Math.floor(seconds/86400)} d ${Math.floor(seconds%86400/3600)} h ${Math.floor(seconds%3600/60)} m ${seconds%60} s`;
  }
  destroy(){this.destroyed=true;this.metadata?.destroy();this.pause();this.controls?.abort();this.life.abort();clearInterval(this.uptime);}
}
class FlopRadioPlayer extends HTMLElement {
  connectedCallback(){
    if(this.controller)return;const root=this.querySelector('.flop-radio-card');if(!root)return;
    const shell=document.querySelector('flop-pwa'),standalone=document.documentElement.classList.contains('flop-standalone')&&shell;
    const labels=JSON.parse(this.querySelector('[data-radio-labels]')?.textContent||'{}');
    if(standalone&&shell.radio){this.querySelector('[data-role="audio"]')?.remove();this.controller=shell.radio;this.controller.attach(root);}
    else{
      const audio=this.querySelector('[data-role="audio"]');
      // Move the idle audio before constructing its controller. A playing media node
      // must never be moved or disconnected during an app screen transition.
      if(standalone)shell.append(audio);
      this.controller=new RadioController(root,labels,audio);
      if(standalone){shell.radio=this.controller;this.controller.show(this.controller.state);}
    }
    this.persistent=Boolean(standalone);
  }
  disconnectedCallback(){if(!this.persistent)this.controller?.destroy();else this.controller?.controls?.abort();this.controller=null;}
}
if(!customElements.get('flop-radio-player'))customElements.define('flop-radio-player',FlopRadioPlayer);
